import { useMemo } from 'react'

import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { api } from '~/api'
import QueryMatcher from '~/components/QueryMatcher'

type Props = {
    sale: {
        shipping_provider: string | null
        tracking_code: string | null
        shop: {
            store: {
                id: number
            }
        }
    }
    onShippingProviderChange: (shippingProvider: string) => void
    onTrackingCodeChange: (trackingCode: string) => void
}

export default function SaleShippingProviders({
    sale,
    onShippingProviderChange,
    onTrackingCodeChange,
}: Props) {
    const query = api.stores.shippingProviders.useQuery(
        ['stores', sale.shop.store.id, 'shipping-providers'],
        {
            params: {
                storeId: sale.shop.store.id,
            },
        }
    )

    const selectedShippingProvider = useMemo(() => {
        if (!query.data) {
            return null
        }

        return query.data.body.results.find((provider) => provider.name === sale.shipping_provider)
    }, [query.data, sale.shipping_provider])

    return (
        <Paper sx={{ p: 2 }}>
            <Typography
                gutterBottom
                sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                }}
            >
                {gettext('Shipping')}
            </Typography>
            <Stack gap={2}>
                <QueryMatcher
                    query={query}
                    Loading={
                        <>
                            <Skeleton variant="rectangular" height={40} />
                            <Skeleton variant="rectangular" height={40} />
                        </>
                    }
                    Errored={<Typography color="error">{gettext('Error')}</Typography>}
                    Empty={null}
                >
                    {({ data }) => (
                        <>
                            <Stack direction="row" alignItems="center" gap={2}>
                                <FormControl sx={{ width: '50%' }} size="small">
                                    <InputLabel id="shipping-provider-label">
                                        {gettext('Shipping Provider (optional)')}
                                    </InputLabel>
                                    <Select
                                        labelId="shipping-provider-label"
                                        label={gettext('Shipping Provider (optional)')}
                                        value={sale.shipping_provider ?? ''}
                                        onChange={(e) =>
                                            onShippingProviderChange(e.target.value ?? '')
                                        }
                                    >
                                        {data.body.results.map((provider) => (
                                            <MenuItem key={provider.id} value={provider.name}>
                                                {provider.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedShippingProvider && (
                                    <img
                                        src={selectedShippingProvider.logo_url}
                                        alt={selectedShippingProvider.name}
                                        height={24}
                                    />
                                )}
                            </Stack>
                            <TextField
                                sx={{ width: '50%' }}
                                size="small"
                                label={gettext('Tracking Code (optional)')}
                                value={sale.tracking_code ?? ''}
                                onChange={(e) => onTrackingCodeChange(e.target.value ?? '')}
                            />
                        </>
                    )}
                </QueryMatcher>
            </Stack>
        </Paper>
    )
}
